import { api } from "../../api/apiSlice";

// Fuel Management (TexasAlarms.Api src/Fuel, base /fuel). Fuel cards are
// prepaid: the office loads money onto a card with a REFILL (submitted, then
// approved - approval is what moves the balance), and drivers spend it with a
// fuel TRANSACTION recorded in the mobile app with two photos. This is NOT a
// trip module - there is no trip, customer or route anywhere in it.
//
// Card <-> vehicle is many-to-many (one default card per vehicle), and a card
// can additionally be authorised for named users.
//
// Tags: anything that moves a card's balance (approving a refill, saving or
// voiding a purchase) invalidates the whole FuelCards type - the list, the
// single card and its ledger all carry the balance. Link changes invalidate
// FuelCards and FuelVehicles too, because both lists carry link counts and the
// default card.
const BALANCE_CHANGED = ["FuelCards"];
const LINKS_CHANGED = ["FuelLinks", "FuelCards", "FuelVehicles"];

export const fuelApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // { fuelTypes, mismatchedCardPolicy, odometerRollbackPolicy }
    getFuelLookups: builder.query({
      query: () => ({ url: "/fuel/lookups" }),
    }),

    // ---- Cards ----
    getFuelCards: builder.query({
      query: (params) => ({ url: "/fuel/cards", params }),
      providesTags: (result) =>
        result
          ? [...result.map((c) => ({ type: "FuelCards", id: c.fuelCardId })), { type: "FuelCards", id: "LIST" }]
          : [{ type: "FuelCards", id: "LIST" }],
    }),
    getFuelCard: builder.query({
      query: (fuelCardId) => ({ url: `/fuel/cards/${fuelCardId}` }),
      providesTags: (result, error, fuelCardId) => [{ type: "FuelCards", id: fuelCardId }],
    }),
    createFuelCard: builder.mutation({
      query: (body) => ({ url: "/fuel/cards", method: "POST", data: body }),
      invalidatesTags: [{ type: "FuelCards", id: "LIST" }],
    }),
    // branchId is ignored by the server on update, and the balance is never
    // editable - it only moves through refills and purchases.
    updateFuelCard: builder.mutation({
      query: ({ fuelCardId, ...body }) => ({ url: `/fuel/cards/${fuelCardId}`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { fuelCardId }) => [
        { type: "FuelCards", id: fuelCardId },
        { type: "FuelCards", id: "LIST" },
        // card number / active flag are repeated on every link row
        "FuelLinks",
        "FuelVehicles",
      ],
    }),
    // Card statement: refills in, purchases out, running balance. Newest first.
    getFuelCardLedger: builder.query({
      query: ({ fuelCardId, ...params }) => ({ url: `/fuel/cards/${fuelCardId}/ledger`, params }),
      providesTags: (result, error, { fuelCardId }) => [{ type: "FuelCards", id: fuelCardId }],
    }),

    // ---- Card <-> vehicle links (many-to-many) ----
    getFuelCardVehicles: builder.query({
      query: (params) => ({ url: "/fuel/card-vehicles", params }),
      providesTags: [{ type: "FuelLinks", id: "VEHICLES" }],
    }),
    // Also the way to flip the default on a link that already exists.
    linkFuelCardVehicle: builder.mutation({
      query: ({ fuelCardId, vehicleId, isDefault }) => ({
        url: `/fuel/cards/${fuelCardId}/vehicles`,
        method: "POST",
        data: { vehicleId, isDefault },
      }),
      invalidatesTags: LINKS_CHANGED,
    }),
    unlinkFuelCardVehicle: builder.mutation({
      query: ({ fuelCardId, vehicleId }) => ({ url: `/fuel/cards/${fuelCardId}/vehicles/${vehicleId}`, method: "DELETE" }),
      invalidatesTags: LINKS_CHANGED,
    }),

    // ---- Card <-> authorised users ----
    getFuelCardUsers: builder.query({
      query: (params) => ({ url: "/fuel/card-users", params }),
      providesTags: [{ type: "FuelLinks", id: "USERS" }],
    }),
    linkFuelCardUser: builder.mutation({
      query: ({ fuelCardId, userId }) => ({ url: `/fuel/cards/${fuelCardId}/users`, method: "POST", data: { userId } }),
      invalidatesTags: ["FuelLinks", "FuelCards"],
    }),
    unlinkFuelCardUser: builder.mutation({
      query: ({ fuelCardId, userId }) => ({ url: `/fuel/cards/${fuelCardId}/users/${userId}`, method: "DELETE" }),
      invalidatesTags: ["FuelLinks", "FuelCards"],
    }),
    // Picker source for the above - max 200 rows, so it is searched server-side.
    getFuelUsers: builder.query({
      query: (params) => ({ url: "/fuel/users", params }),
    }),

    // ---- Vehicles (fuel profile) ----
    getFuelVehicles: builder.query({
      query: (params) => ({ url: "/fuel/vehicles", params }),
      providesTags: [{ type: "FuelVehicles", id: "LIST" }],
    }),
    updateFuelVehicleProfile: builder.mutation({
      query: ({ vehicleId, ...body }) => ({ url: `/fuel/vehicles/${vehicleId}/profile`, method: "PUT", data: body }),
      invalidatesTags: [{ type: "FuelVehicles", id: "LIST" }],
    }),

    // ---- Field entry (the mobile app's half; here for completeness) ----
    getFuelEntryVehicles: builder.query({
      query: () => ({ url: "/fuel/entry/vehicles" }),
      providesTags: [{ type: "FuelVehicles", id: "ENTRY" }],
    }),
    getFuelEntryCards: builder.query({
      query: (vehicleId) => ({ url: "/fuel/entry/cards", params: { vehicleId } }),
      providesTags: ["FuelLinks", { type: "FuelCards", id: "LIST" }],
    }),

    // ---- Refills (money loaded onto a card by the office) ----
    listFuelRefills: builder.query({
      query: (params) => ({ url: "/fuel/refills", params }),
      providesTags: [{ type: "FuelRefills", id: "LIST" }],
    }),
    getFuelRefill: builder.query({
      query: (recordGuid) => ({ url: `/fuel/refills/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "FuelRefills", id: recordGuid }],
    }),
    createFuelRefill: builder.mutation({
      query: (body) => ({ url: "/fuel/refills", method: "POST", data: body }),
      invalidatesTags: [{ type: "FuelRefills", id: "LIST" }],
    }),
    // Approval is what loads the balance (and stamps previous/new balance).
    approveFuelRefill: builder.mutation({
      query: ({ recordGuid, approvalComment }) => ({
        url: `/fuel/refills/${recordGuid}/approve`,
        method: "POST",
        data: { approvalComment },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "FuelRefills", id: recordGuid },
        { type: "FuelRefills", id: "LIST" },
        ...BALANCE_CHANGED,
      ],
    }),
    rejectFuelRefill: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/fuel/refills/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "FuelRefills", id: recordGuid },
        { type: "FuelRefills", id: "LIST" },
      ],
    }),
    // jpeg/png/pdf up to 2MB, only while the refill is still SUBMITTED.
    uploadFuelRefillAttachment: builder.mutation({
      query: ({ recordGuid, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: `/fuel/refills/${recordGuid}/attachment`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "FuelRefills", id: recordGuid },
        { type: "FuelRefills", id: "LIST" },
      ],
    }),
    // Authenticated file - fetched as a Blob (axiosBaseQuery passes responseType through).
    getFuelRefillAttachment: builder.query({
      query: (recordGuid) => ({ url: `/fuel/refills/${recordGuid}/attachment`, responseType: "blob" }),
      keepUnusedDataFor: 600,
      providesTags: (result, error, recordGuid) => [{ type: "FuelRefills", id: recordGuid }],
    }),

    // ---- Transactions (fuel purchases) ----
    // Recorded from the mobile app; multipart, numbers as plain invariant text.
    // The server sets the date/time, the total and the user. No edit endpoint
    // exists, by design - a wrong entry is voided and re-entered.
    createFuelTransaction: builder.mutation({
      query: ({ odometerImage, pumpImage, ...fields }) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") formData.append(key, String(value));
        });
        formData.append("odometerImage", odometerImage);
        formData.append("pumpImage", pumpImage);
        return { url: "/fuel/transactions", method: "POST", data: formData };
      },
      invalidatesTags: [{ type: "FuelTransactions", id: "LIST" }, { type: "FuelVehicles", id: "LIST" }, ...BALANCE_CHANGED],
    }),
    listFuelTransactions: builder.query({
      query: (params) => ({ url: "/fuel/transactions", params }),
      providesTags: [{ type: "FuelTransactions", id: "LIST" }],
    }),
    // { transaction, exceptions }
    getFuelTransaction: builder.query({
      query: (recordGuid) => ({ url: `/fuel/transactions/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "FuelTransactions", id: recordGuid }],
    }),
    // kind: "odometer" | "pump". The photos never change once saved, so they
    // carry no tag and are simply kept for a while.
    getFuelTransactionImage: builder.query({
      query: ({ recordGuid, kind }) => ({ url: `/fuel/transactions/${recordGuid}/images/${kind}`, responseType: "blob" }),
      keepUnusedDataFor: 600,
    }),
    reviewFuelTransaction: builder.mutation({
      query: ({ recordGuid, reviewRemarks }) => ({
        url: `/fuel/transactions/${recordGuid}/review`,
        method: "POST",
        data: { reviewRemarks },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "FuelTransactions", id: recordGuid },
        { type: "FuelTransactions", id: "LIST" },
      ],
    }),
    // Voiding returns the amount to the card; the entry itself stays on record.
    voidFuelTransaction: builder.mutation({
      query: ({ recordGuid, voidReason }) => ({
        url: `/fuel/transactions/${recordGuid}/void`,
        method: "POST",
        data: { voidReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "FuelTransactions", id: recordGuid },
        { type: "FuelTransactions", id: "LIST" },
        { type: "FuelVehicles", id: "LIST" },
        ...BALANCE_CHANGED,
      ],
    }),

    // ---- Reports ---- (voided purchases are excluded server-side)
    getFuelReportSummary: builder.query({
      query: (params) => ({ url: "/fuel/reports/summary", params }),
      providesTags: [{ type: "FuelTransactions", id: "LIST" }],
    }),
    // Month-wise day grid (Batch197): long rows (vehicle / FUEL|REFILL / day), pivoted
    // on the Fuel Monthly Summary page. Refills change it too, so both tags.
    getFuelMonthGrid: builder.query({
      query: (params) => ({ url: "/fuel/reports/month-grid", params }),
      providesTags: [{ type: "FuelTransactions", id: "LIST" }, { type: "FuelRefills", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFuelLookupsQuery,
  useGetFuelCardsQuery,
  useGetFuelCardQuery,
  useCreateFuelCardMutation,
  useUpdateFuelCardMutation,
  useGetFuelCardLedgerQuery,
  useGetFuelCardVehiclesQuery,
  useLinkFuelCardVehicleMutation,
  useUnlinkFuelCardVehicleMutation,
  useGetFuelCardUsersQuery,
  useLinkFuelCardUserMutation,
  useUnlinkFuelCardUserMutation,
  useGetFuelUsersQuery,
  useGetFuelVehiclesQuery,
  useUpdateFuelVehicleProfileMutation,
  useGetFuelEntryVehiclesQuery,
  useGetFuelEntryCardsQuery,
  useListFuelRefillsQuery,
  useGetFuelRefillQuery,
  useCreateFuelRefillMutation,
  useApproveFuelRefillMutation,
  useRejectFuelRefillMutation,
  useUploadFuelRefillAttachmentMutation,
  useGetFuelRefillAttachmentQuery,
  useLazyGetFuelRefillAttachmentQuery,
  useCreateFuelTransactionMutation,
  useListFuelTransactionsQuery,
  useGetFuelTransactionQuery,
  useGetFuelTransactionImageQuery,
  useReviewFuelTransactionMutation,
  useVoidFuelTransactionMutation,
  useGetFuelReportSummaryQuery,
  useGetFuelMonthGridQuery,
} = fuelApi;
